
import { computed, defineComponent, onBeforeMount, PropType, reactive, ref } from 'vue'

// @components
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'

import exportIcon from '@/assets/icons/export.svg'
import uploadIcon from '@/assets/icons/upload.svg'
import excelIcon from '@/assets/icons/excel.svg'
import timesGreyIcon from '@/assets/icons/timesGrey.svg'

// @types
import { CityT } from '@/core/types/cities.types'
import {
    AddDirectionTMSPayload,
    CheckImportFileItem,
    DirectionFullTms,
    DirectionTMS,
} from '@/new-services/types/tms'
import { Zone } from '@/new-services/types/location'

// @services
import locationAPI from '@/new-services/location.service'
import tmsAPI from '@/new-services/tms.service'

// @validations
import useVuelidate from '@vuelidate/core'
import { integer, required } from '@vuelidate/validators'
import { UserT } from '@/core/types/users.types'
import { DataState } from '@/core/classes/DataState.class'
import { users } from '@/core/realisations/users.realisation'
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

const TABS = [
    { id: 1, label: 'Направление' },
    { id: 2, label: 'SLA' },
    { id: 3, label: 'Нормы загруженности' },
    { id: 4, label: 'Дочерние направления' },
]

const SLA_TABS = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

export default defineComponent({
    components: {
        NewModal,
        NewCheckbox,
        NewSelect,
        NewInput,
        Loader,
        exportIcon,
        uploadIcon,
        excelIcon,
        timesGreyIcon,
        RadioGroup,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<DirectionTMS>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            excel_response: [] as CheckImportFileItem[],
            direction: null as DirectionFullTms | null,
            current_tab_sla: 1,
        })
        const tabs_state = reactive({
            id: 1,
        })

        const isLtlSla = computed(() => state.current_tab_sla === 1)
        const isFtlSla = computed(() => state.current_tab_sla === 2)

        const payload = reactive({
            fromCityId: null as number | null,
            whereCityId: null as number | null,
            zoneId: null as number | null,
            logistId: null as number | null,
            isActive: 1,
            //
            sla: {
                ltl: [
                    {
                        id: null as number | null,
                        type: 1,
                        periodId: 1,
                        supplierId: null as number | null,
                        tariff: 'standart',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                    {
                        id: null as number | null,
                        type: 1,
                        periodId: 2,
                        supplierId: null as number | null,
                        tariff: 'standart',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                    {
                        id: null as number | null,
                        type: 1,
                        periodId: 1,
                        supplierId: null as number | null,
                        tariff: 'express',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                    {
                        id: null as number | null,
                        type: 1,
                        periodId: 2,
                        supplierId: null as number | null,
                        tariff: 'express',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                ],
                ftl: [
                    {
                        id: null as number | null,
                        type: 2,
                        periodId: 1,
                        supplierId: null as number | null,
                        tariff: 'standart',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                    {
                        id: null as number | null,
                        type: 2,
                        periodId: 2,
                        supplierId: null as number | null,
                        tariff: 'standart',
                        pickup: null as number | null,
                        processing: null as number | null,
                        transit: null as number | null,
                        delivery: null as number | null,
                    },
                ],
            },
            //
            capacities: [
                {
                    id: 6,
                    weightLoadStandard: null as number | null,
                    cubatureLoadStandard: null as number | null,
                },
                {
                    id: 5,
                    weightLoadStandard: null as number | null,
                    cubatureLoadStandard: null as number | null,
                },
                {
                    id: 3,
                    weightLoadStandard: null as number | null,
                    cubatureLoadStandard: null as number | null,
                },
                {
                    id: 2,
                    weightLoadStandard: null as number | null,
                    cubatureLoadStandard: null as number | null,
                },
                {
                    id: 1,
                    weightLoadStandard: null as number | null,
                    cubatureLoadStandard: null as number | null,
                },
            ],
            //
            internalDirections: [] as { directionId: number }[],
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            zones: {
                is_loading: false,
                is_failed: false,
                list: [] as Zone[],
            },
            logists: new DataState<UserT>({}),
        })

        const DirectionTMSValidation = {
            fromCityId: { required, integer },
            whereCityId: { required, integer },
            zoneId: { required, integer },
        }

        const validation$ = useVuelidate(DirectionTMSValidation, payload)

        //

        const getPayload = () => {
            const payloadEdited: AddDirectionTMSPayload = {
                ...payload,
                sla: [...payload.sla.ltl, ...payload.sla.ftl],
                capacities: payload.capacities.map((item) => {
                    return {
                        id: item.id,
                        weightLoadStandard: Number(item.weightLoadStandard),
                        cubatureLoadStandard: Number(item.cubatureLoadStandard),
                    }
                }),
            }
            payloadEdited.sla = payloadEdited.sla.map((item) => {
                return {
                    ...item,
                    supplierId: item.supplierId ? Number(item.supplierId) : item.supplierId,
                    pickup: Number(item.pickup),
                    processing: Number(item.processing),
                    transit: Number(item.transit),
                    delivery: Number(item.delivery),
                }
            })
            payloadEdited.sla = payloadEdited.sla.filter(
                (item) => item.pickup && item.processing && item.transit && item.delivery
            )

            const directions = state.excel_response.filter((direction) => direction.isSuccess)

            if (directions.length) {
                payloadEdited.internalDirections = directions.map((item) => {
                    return {
                        directionId: item.directionId,
                    }
                })
            }

            return {
                ...payloadEdited,
            }
        }

        const file = ref(null)
        const isDragging = ref(false)

        const { getFormData } = useFormData()

        const isTariffReady = computed(() => {
            const standartTariffLtl = payload.sla.ltl.filter((item) => item.tariff === 'standart')
            const expressTariffLtl = payload.sla.ltl.filter((item) => item.tariff === 'express')

            const isStandartReadyLtl = standartTariffLtl.find(
                (item) => item.pickup && item.processing && item.transit && item.delivery
            )
            const isExpressReadyLtl = expressTariffLtl.find(
                (item) => item.pickup && item.processing && item.transit && item.delivery
            )

            // FTL

            const standartTariffFtl = payload.sla.ftl.filter((item) => item.tariff === 'standart')

            const isStandartReadyFtl = standartTariffFtl.find(
                (item) => item.pickup && item.processing && item.transit && item.delivery
            )

            return Boolean(isStandartReadyLtl || isExpressReadyLtl || isStandartReadyFtl)
        })

        const showErrors = () => {
            if (!payload.fromCityId || !payload.whereCityId || !payload.zoneId) {
                notifications.notify({
                    title: 'Проверьте таб "Направление"',
                    description: 'Заполните необходимые поля',
                    type: 'danger',
                })
            }

            if (!isTariffReady.value) {
                notifications.notify({
                    title: 'Проверьте таб "SLA"',
                    description: 'Все поля как минимум одного из тарифов должны быть заполнены',
                    type: 'danger',
                })
            }
        }

        const emitCreate = () => emit('create', getPayload())
        const emitUpdate = () => emit('update', getPayload())

        const onSubmitExcel = () => {
            if (props.isCreate) emitCreate()
            else if (props.isUpdate) emitUpdate()
        }

        const manageEmit = async () => {
            validation$.value.$touch()
            if (validation$.value.$invalid || !isTariffReady.value) {
                showErrors()
                return
            }

            try {
                state.is_loading = true

                if (file.value) {
                    const response = await tmsAPI.checkImportFile(getFormData({ file: file.value }))
                    state.excel_response = response.data.data
                } else {
                    onSubmitExcel()
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при загрузке файла`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const emitCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const emitManager = () => {
            if (props.loading) return

            manageEmit()
        }

        // cities

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities(1)
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        // zones

        const loadZones = async () => {
            try {
                data_state.zones.is_loading = true

                const response = await locationAPI.getZones()
                data_state.zones.list = response.data.data

                data_state.zones.is_failed = false
            } catch (error) {
                data_state.zones.is_failed = true
                console.error(error)
            } finally {
                data_state.zones.is_loading = false
            }
        }

        const loadSla = async () => {
            if (!state.direction || !state.direction.fromCity || !state.direction.whereCity) return

            try {
                state.is_loading = true

                const responseSlaLtl = await tmsAPI.getDirectionSlaLtl(
                    state.direction.fromCity.id,
                    state.direction.whereCity.id
                )
                const responseSlaFtl = await tmsAPI.getDirectionSlaFtl(
                    state.direction.fromCity.id,
                    state.direction.whereCity.id
                )

                if (responseSlaFtl && responseSlaFtl.data.data.length) {
                    const ftlSla = responseSlaFtl.data.data
                    ftlSla.forEach((slaItem) => {
                        const itemFound = payload.sla.ftl.find(
                            (sla) =>
                                sla.periodId === slaItem.periodId &&
                                sla.tariff === slaItem.tariffCode
                        )
                        if (itemFound) {
                            itemFound.id = slaItem.id
                            itemFound.supplierId = slaItem.supplierId

                            itemFound.pickup = slaItem.pickup
                            itemFound.processing = slaItem.processing
                            itemFound.transit = slaItem.transit
                            itemFound.delivery = slaItem.delivery
                        }
                    })
                }
                if (responseSlaLtl && responseSlaLtl.data.data.length) {
                    const ltlSla = responseSlaLtl.data.data
                    ltlSla.forEach((slaItem) => {
                        const itemFound = payload.sla.ltl.find(
                            (sla) =>
                                sla.periodId === slaItem.periodId &&
                                sla.tariff === slaItem.tariffCode
                        )
                        if (itemFound) {
                            itemFound.id = slaItem.id
                            itemFound.supplierId = slaItem.supplierId

                            itemFound.pickup = slaItem.pickup
                            itemFound.processing = slaItem.processing
                            itemFound.transit = slaItem.transit
                            itemFound.delivery = slaItem.delivery
                        }
                    })
                }
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const setDirection = () => {
            if (!state.direction) return

            if (state.direction.fromCity) {
                payload.fromCityId = state.direction.fromCity.id
            }
            if (state.direction.whereCity) {
                payload.whereCityId = state.direction.whereCity.id
            }
            if (state.direction.zone) {
                payload.zoneId = state.direction.zone.id
            }
            payload.logistId = state.direction.logistId
            payload.isActive = Number(state.direction.isActive)

            if (
                state.direction.loadStandard &&
                state.direction.loadStandard.capacityData &&
                state.direction.loadStandard.capacityData.length
            ) {
                payload.capacities = state.direction.loadStandard.capacityData.map((item) => {
                    return {
                        id: item.id,
                        weightLoadStandard: item.weightLoadStandard,
                        cubatureLoadStandard: item.cubatureLoadStandard,
                    }
                })
                payload.capacities
            }

            if (state.direction.internalDirections && state.direction.internalDirections.length) {
                payload.internalDirections = state.direction.internalDirections.map((item) => {
                    return {
                        directionId: item.directionId,
                    }
                })
            }

            loadSla()
        }

        const loadDirection = async () => {
            const { id } = props.initialValue

            try {
                state.is_loading = true

                const response = await tmsAPI.getDirectionTMS(id)
                state.direction = response.data.data

                setDirection()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const setInfo = () => {
            if (props.initialValue) {
                loadDirection()
            }
        }

        const selectTab = (tab: { id: number }) => (tabs_state.id = tab.id)

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name, undefined, 1)

                data_state.logists.list = logists.data
                data_state.logists.pagination.total_pages = logists.meta.last_page
                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page)

                data_state.logists.list = data_state.logists.list.concat(logists.data)
                data_state.logists.pagination.total_pages = logists.meta.last_page
                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        const isSubRow = (index: number) => index % 2 === 0

        //

        const title = computed(() => {
            return props.isCreate ? 'Создание направления' : 'Редактирование направления'
        })
        const isDirection = computed(() => tabs_state.id === 1)
        const isSla = computed(() => tabs_state.id === 2)
        const isCost = computed(() => tabs_state.id === 3)
        const isInternal = computed(() => tabs_state.id === 4)

        //

        onBeforeMount(() => {
            loadCities()
            loadZones()
            setInfo()
            loadLogists(1)
        })

        //

        const handleFile = (e: any) => {
            const selected = e.target.files[0]
            if (selected) file.value = selected
        }

        const handleDrop = (e: any) => {
            isDragging.value = false
            const dropped = e.dataTransfer.files[0]
            if (dropped) file.value = dropped
        }

        const removeFile = () => {
            file.value = null
        }

        const formatSize = (size: number) => {
            const kb = size / 1024
            if (kb < 1024) return `${kb.toFixed(1)} KB`
            return `${(kb / 1024).toFixed(1)} MB`
        }

        const cancelExcel = () => {
            state.excel_response = []
        }

        //

        return {
            state,
            payload,
            data_state,
            tabs_state,
            validation$,
            loadCities,
            loadZones,
            emitCancel,
            emitManager,
            selectTab,
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            isDirection,
            isSla,
            isCost,
            isInternal,
            isSubRow,
            title,
            TABS,
            file,
            isDragging,
            handleFile,
            handleDrop,
            removeFile,
            formatSize,
            cancelExcel,
            onSubmitExcel,
            SLA_TABS,
            isLtlSla,
            isFtlSla,
        }
    },
})
